import { useMutation } from '@tanstack/react-query';

import { Collections, ShortUrl } from '@air/api';
import { type FullWorkspace } from '@air/api/types';
import { type ApiResponse, callAirApi } from '@air/utils-api';

// `Collections.create` now also accepts a batch shape ({ workspaceId, collections: [...] }, FOUND-643),
// but this hook only ever creates one collection, so narrow to the single arm — `name` only appears
// at the top level on the single arm (the batch arm has it nested inside `collections`).
type CreateCollectionSingleArgs = Extract<Parameters<typeof Collections.create>[0], { name: string }>;

type CreateCollectionResponse = ApiResponse<
  (args: CreateCollectionSingleArgs) => ReturnType<typeof Collections.create>
>;

import { queryClient } from '~/swr-hooks/queryClient';
import { isShortUrlsListKey } from '~/swr-hooks/shortUrl/useListShortUrls';

export type UseCreateCollectionMutationParams = {
  workspaceId: FullWorkspace['id'];
  collection: Omit<CreateCollectionSingleArgs, 'workspaceId'>;
  shortUrl: Omit<Parameters<typeof ShortUrl.getShortIdForCollection>[0], 'workspaceId' | 'id'>;
};

export type UseCreateCollectionMutationReturn = {
  collection: Awaited<ReturnType<typeof Collections.create>>;
  shortUrl: Awaited<ReturnType<typeof ShortUrl.getShortIdForCollection>>;
};

export const useCreateCollectionMutation = () => {
  return useMutation<UseCreateCollectionMutationReturn, unknown, UseCreateCollectionMutationParams>({
    mutationFn: async ({ workspaceId, collection, shortUrl }) => {
      const createdCollection = await callAirApi<CreateCollectionResponse>(() =>
        Collections.create({
          workspaceId,
          ...collection,
        }),
      );

      if (!createdCollection.id) {
        throw new Error('Collection was created but no ID was returned');
      }

      // Create short URL for the collection
      const createdShortUrl = await ShortUrl.getShortIdForCollection({
        workspaceId,
        id: createdCollection.id,
        ...shortUrl,
      });

      return {
        collection: createdCollection,
        shortUrl: createdShortUrl,
      };
    },
    onSettled: (_data, _error, { workspaceId }) => {
      if (workspaceId) {
        // Predicate, not a key literal: getShortUrlsListKey({ workspaceId }) alone builds a key whose
        // search/limit/orderBy are undefined, which partial-matching compares against the mounted
        // list's REAL values and never matches — the invalidation was a silent no-op.
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) => isShortUrlsListKey(queryKey) && queryKey[1].workspaceId === workspaceId,
        });
      }
    },
  });
};
